/**
 * SideSection — one block of the Piece screen's side column (FEAT-087).
 *
 * The column is the screen's information surface: everything about the piece
 * that is not the piece itself lives here, as a section. Only documents open
 * in the main window. Sections either stand open (the design's Pipeline and
 * How it got here) or collapse behind their own header.
 */

import type { ReactNode } from "react";

export interface SideSectionProps {
  title: string;
  /** Right-aligned count in the header, as the design's trail groups show. */
  count?: number | string;
  /** A text control on the header's right — the design's "Show detail". */
  action?: { label: string; onClick: () => void; pressed?: boolean };
  /** One line under the header explaining what the section is for. */
  lede?: string;
  /** When set the whole section collapses; the header becomes the toggle. */
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}

export function SideSection({
  title,
  count,
  action,
  lede,
  collapsible = false,
  open = true,
  onToggle,
  children,
}: SideSectionProps) {
  const head = (
    <>
      {collapsible && (
        <span className={`pside-caret ${open ? "is-open" : ""}`} aria-hidden>
          ›
        </span>
      )}
      <span className="pside-title">{title}</span>
      {count !== undefined && <span className="pside-count">{count}</span>}
    </>
  );

  return (
    <section className={`pside-block ${collapsible ? "is-collapsible" : ""}`}>
      <div className="pside-head">
        {collapsible ? (
          <button
            type="button"
            className="pside-head-btn"
            aria-expanded={open}
            onClick={onToggle}
          >
            {head}
          </button>
        ) : (
          head
        )}
        {action && (
          <button
            type="button"
            className="pside-action"
            onClick={action.onClick}
            aria-expanded={action.pressed}
          >
            {action.label}
          </button>
        )}
      </div>
      {open && (
        <>
          {lede && <p className="pside-lede">{lede}</p>}
          {children}
        </>
      )}
    </section>
  );
}
